/**
 * Performance Monitoring System
 * Tracks and reports Core Web Vitals and custom performance metrics
 * 
 * Metrics tracked:
 * - LCP (Largest Contentful Paint)
 * - FID (First Input Delay)
 * - CLS (Cumulative Layout Shift)
 * - FCP (First Contentful Paint)
 * - TTFB (Time to First Byte)
 * - Custom: Slider Init Time, Image Load Time
 */

// DEBUG MODE: Set to true to enable performance logging, false for production
const PERFORMANCE_DEBUG_MODE = false;

// Exit early if debug mode is disabled
if (!PERFORMANCE_DEBUG_MODE) {
  // Export empty monitor for compatibility
  if (typeof window !== 'undefined') {
    window.performanceMonitor = {
      init: () => {},
      logMetric: () => {}
    };
  }
} else {

class PerformanceMonitor {
  constructor() {
    this.metrics = {};
    this.observers = [];
    this.startTime = performance.now();
  }

  /**
   * Initialize performance monitoring
   */
  init() {
    // Core Web Vitals
    this.observeLCP();
    this.observeFID();
    this.observeCLS();
    
    // Additional metrics
    this.measureFCP();
    this.measureTTFB();
    
    // Custom metrics
    this.trackSliderPerformance();
    this.trackImageLoading();
    
    // Report after page load
    this.scheduleReport();
  }

  /**
   * Observe Largest Contentful Paint (LCP)
   */
  observeLCP() {
    try {
      const observer = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        const lastEntry = entries[entries.length - 1];
        
        this.metrics.lcp = {
          value: lastEntry.renderTime || lastEntry.loadTime,
          element: lastEntry.element?.tagName || 'unknown',
          rating: this.rateLCP(lastEntry.renderTime || lastEntry.loadTime)
        };
      });
      
      observer.observe({ type: 'largest-contentful-paint', buffered: true });
      this.observers.push(observer);
    } catch (e) {
      console.warn('LCP monitoring not supported');
    }
  }

  /**
   * Observe First Input Delay (FID)
   */
  observeFID() {
    try {
      const observer = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        entries.forEach(entry => {
          this.metrics.fid = {
            value: entry.processingStart - entry.startTime,
            rating: this.rateFID(entry.processingStart - entry.startTime)
          };
        });
      });
      
      observer.observe({ type: 'first-input', buffered: true });
      this.observers.push(observer);
    } catch (e) {
      console.warn('FID monitoring not supported');
    }
  }

  /**
   * Observe Cumulative Layout Shift (CLS)
   */
  observeCLS() {
    try {
      let clsValue = 0;
      
      const observer = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        entries.forEach(entry => {
          if (!entry.hadRecentInput) {
            clsValue += entry.value;
          }
        });
        
        this.metrics.cls = {
          value: clsValue,
          rating: this.rateCLS(clsValue)
        };
      });
      
      observer.observe({ type: 'layout-shift', buffered: true });
      this.observers.push(observer);
    } catch (e) {
      console.warn('CLS monitoring not supported');
    }
  }

  /**
   * Measure First Contentful Paint (FCP)
   */
  measureFCP() {
    try {
      const fcpEntry = performance.getEntriesByName('first-contentful-paint')[0];
      if (fcpEntry) {
        this.metrics.fcp = {
          value: fcpEntry.startTime,
          rating: this.rateFCP(fcpEntry.startTime)
        };
      }
    } catch (e) {
      console.warn('FCP measurement not available');
    }
  }

  /**
   * Measure Time to First Byte (TTFB)
   */
  measureTTFB() {
    try {
      const navigationEntry = performance.getEntriesByType('navigation')[0];
      if (navigationEntry) {
        this.metrics.ttfb = {
          value: navigationEntry.responseStart - navigationEntry.requestStart,
          rating: this.rateTTFB(navigationEntry.responseStart - navigationEntry.requestStart)
        };
      }
    } catch (e) {
      console.warn('TTFB measurement not available');
    }
  }

  /**
   * Track slider initialization performance
   */
  trackSliderPerformance() {
    // Listen for slider initialization events
    const sliderStartTime = performance.now();
    let slidersInitialized = 0;
    
    document.addEventListener('slider:init', (e) => {
      slidersInitialized++;
      
      if (slidersInitialized === 1) {
        // First slider initialized (hero)
        this.metrics.firstSliderInit = {
          value: performance.now() - sliderStartTime,
          count: 1
        };
      }
    });
    
    // Check after 5 seconds for total sliders
    setTimeout(() => {
      const allSliders = document.querySelectorAll('[data-slider]');
      this.metrics.sliderStats = {
        total: allSliders.length,
        initialized: slidersInitialized,
        firstSliderTime: this.metrics.firstSliderInit?.value || 0
      };
    }, 5000);
  }

  /**
   * Track image loading performance
   */
  trackImageLoading() {
    const images = document.querySelectorAll('img');
    let loadedCount = 0;
    let errorCount = 0;
    const startTime = performance.now();
    
    const checkComplete = () => {
      const totalImages = images.length;
      if (loadedCount + errorCount >= totalImages) {
        this.metrics.imageLoading = {
          total: totalImages,
          loaded: loadedCount,
          errors: errorCount,
          duration: performance.now() - startTime
        };
      }
    };
    
    images.forEach(img => {
      if (img.complete) {
        loadedCount++;
      } else {
        img.addEventListener('load', () => {
          loadedCount++;
          checkComplete();
        }, { once: true });
        
        img.addEventListener('error', () => {
          errorCount++;
          checkComplete();
        }, { once: true });
      }
    });
    
    checkComplete();
  }

  /**
   * Rate LCP performance
   */
  rateLCP(value) {
    if (value <= 2500) return 'good';
    if (value <= 4000) return 'needs-improvement';
    return 'poor';
  }

  /**
   * Rate FID performance
   */
  rateFID(value) {
    if (value <= 100) return 'good';
    if (value <= 300) return 'needs-improvement';
    return 'poor';
  }

  /**
   * Rate CLS performance
   */
  rateCLS(value) {
    if (value <= 0.1) return 'good';
    if (value <= 0.25) return 'needs-improvement';
    return 'poor';
  }

  /**
   * Rate FCP performance
   */
  rateFCP(value) {
    if (value <= 1800) return 'good';
    if (value <= 3000) return 'needs-improvement';
    return 'poor';
  }

  /**
   * Rate TTFB performance
   */
  rateTTFB(value) {
    if (value <= 800) return 'good';
    if (value <= 1800) return 'needs-improvement';
    return 'poor';
  }

  /**
   * Schedule performance report
   */
  scheduleReport() {
    // Report after load event
    window.addEventListener('load', () => {
      setTimeout(() => {
        this.report();
      }, 3000);
    });
  }

  /**
   * Generate and log performance report
   */
  report() {
    const report = {
      url: window.location.href,
      timestamp: new Date().toISOString(),
      metrics: this.metrics,
      connection: this.getConnectionInfo(),
      device: this.getDeviceInfo()
    };
    
    // Log to console
    console.group('🚀 Performance Report');
    console.log('Core Web Vitals:');
    if (report.metrics.lcp) {
      console.log(`  LCP: ${report.metrics.lcp.value.toFixed(0)}ms (${report.metrics.lcp.rating}) - ${report.metrics.lcp.element}`);
    }
    if (report.metrics.fid) {
      console.log(`  FID: ${report.metrics.fid.value.toFixed(0)}ms (${report.metrics.fid.rating})`);
    }
    if (report.metrics.cls) {
      console.log(`  CLS: ${report.metrics.cls.value.toFixed(3)} (${report.metrics.cls.rating})`);
    }
    
    console.log('\nLoad Metrics:');
    if (report.metrics.fcp) {
      console.log(`  FCP: ${report.metrics.fcp.value.toFixed(0)}ms (${report.metrics.fcp.rating})`);
    }
    if (report.metrics.ttfb) {
      console.log(`  TTFB: ${report.metrics.ttfb.value.toFixed(0)}ms (${report.metrics.ttfb.rating})`);
    }
    
    if (report.metrics.sliderStats) {
      console.log('\nSlider Performance:');
      console.log(`  Total sliders: ${report.metrics.sliderStats.total}`);
      console.log(`  Initialized: ${report.metrics.sliderStats.initialized}`);
      console.log(`  First slider: ${report.metrics.sliderStats.firstSliderTime.toFixed(0)}ms`);
    }
    
    if (report.metrics.imageLoading) {
      console.log('\nImage Loading:');
      console.log(`  Total images: ${report.metrics.imageLoading.total}`);
      console.log(`  Loaded: ${report.metrics.imageLoading.loaded}`);
      console.log(`  Errors: ${report.metrics.imageLoading.errors}`);
      console.log(`  Duration: ${report.metrics.imageLoading.duration.toFixed(0)}ms`);
    }
    
    console.log('\nDevice:', report.device);
    console.log('Connection:', report.connection);
    console.groupEnd();
    
    // Store for analytics (optional)
    window.performanceReport = report;
    
    // You can send this to your analytics service
    // this.sendToAnalytics(report);
  }

  /**
   * Get connection information
   */
  getConnectionInfo() {
    const conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    if (!conn) return 'unknown';
    
    return {
      effectiveType: conn.effectiveType,
      downlink: conn.downlink,
      rtt: conn.rtt,
      saveData: conn.saveData
    };
  }

  /**
   * Get device information
   */
  getDeviceInfo() {
    return {
      userAgent: navigator.userAgent,
      viewport: `${window.innerWidth}x${window.innerHeight}`,
      pixelRatio: window.devicePixelRatio,
      cores: navigator.hardwareConcurrency || 'unknown',
      memory: navigator.deviceMemory || 'unknown'
    };
  }

  /**
   * Cleanup
   */
  destroy() {
    this.observers.forEach(observer => observer.disconnect());
    this.observers = [];
  }
}

// Create singleton
window.performanceMonitor = window.performanceMonitor || new PerformanceMonitor();

// Initialize immediately
window.performanceMonitor.init();

// Export for module usage
if (typeof module !== 'undefined' && module.exports) {
  module.exports = PerformanceMonitor;
}

} // End of PERFORMANCE_DEBUG_MODE check

